import React from 'react';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/browser';
import { onCLS, onFID, onFCP, onLCP, onTTFB } from 'web-vitals';

// Initialize error monitoring
export const initializeMonitoring = () => {
  if (import.meta.env.PROD) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.MODE,
      tracesSampleRate: 1.0,
      integrations: [
        new BrowserTracing({
          tracePropagationTargets: [
            "localhost",
            import.meta.env.VITE_SUPABASE_URL,
          ],
        }),
      ],
    });
  }
};

// Performance monitoring
export const measurePerformance = () => {
  if ('performance' in window) {
    // Report Core Web Vitals
    onCLS(metric => sendToAnalytics('CLS', metric));
    onFID(metric => sendToAnalytics('FID', metric));
    onFCP(metric => sendToAnalytics('FCP', metric));
    onLCP(metric => sendToAnalytics('LCP', metric));
    onTTFB(metric => sendToAnalytics('TTFB', metric));
  }
};

// Helper function to send metrics
const sendToAnalytics = (name: string, metric: { value: number; id: string }) => {
  if (import.meta.env.DEV) {
    console.log(`${name}: ${metric.value} (ID: ${metric.id})`);
  } else {
    Sentry.addBreadcrumb({
      category: 'web-vitals',
      message: `${name}: ${metric.value}`,
      level: 'info',
    });
  }
};

// Error boundary component
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by boundary:', error, errorInfo);
    Sentry.captureException(error, { extra: errorInfo });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="glass-panel p-8 max-w-md w-full mx-auto text-center space-y-4">
            <h2 className="text-2xl font-bold text-red-400">Something went wrong</h2>
            <p className="text-gray-300">
              We've been notified and are working to fix the issue.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="tech-button"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}